/* eslint-disable no-restricted-syntax */
/* eslint-disable class-methods-use-this */
import SimpleReactValidator from 'simple-react-validator';
import { i18n } from '@lingui/core';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_LENGTH_REGEX,
  PASSWORD_REGEX,
} from '../lib/Password';
import { scrollToError } from '../lib/Scroll';
import { DEFAULT_LANGUAGE } from '../lib/Language';
import { translationByLang } from '../lib/hook/useI18n';

declare global {
  interface Window {
    intlTelInputUtils: any;
  }
}

export default class FormValidatorHelper {
  form: any;

  scrollOnInvalid: boolean;

  constructor(
    component?: React.ReactNode,
    scrollOnInvalid = false,
    defaultLang = i18n.locale
  ) {
    // only uses translations if i18n is called
    const messages = i18n.locale
      ? {
          required: translationByLang('validator.required', defaultLang),
          email: translationByLang('validator.email', defaultLang),
        }
      : {};
    this.form = new SimpleReactValidator({
      autoForceUpdate: component || this,
      locale: DEFAULT_LANGUAGE.value,
      validators: this.customRules(),
      messages,
    });
    this.scrollOnInvalid = scrollOnInvalid;
  }

  private customRules(): any {
    return {
      noNumbers: {
        message: 'The :attribute should not have numbers',
        rule: (val: any, params: any, validator: any) =>
          validator.helpers.testRegex(val, /^([^0-9]*)$/) &&
          params.indexOf(val) === -1,
      },
      passwordConstraint: {
        message: 'The :attribute does not meet requirements',
        rule: (val: any, params: any, validator: any) =>
          validator.helpers.testRegex(val, PASSWORD_REGEX),
      },
      passwordLengthConstraint: {
        message: `Please use at least ${MIN_PASSWORD_LENGTH} characters`,
        rule: (val: any, params: any, validator: any) =>
          validator.helpers.testRegex(val, PASSWORD_LENGTH_REGEX),
      },
      phoneFormat: {
        message: 'The :attribute is invalid',
        rule: (val: string) =>
          /**
           * Using react-intl-tel-input service to validate the phone number
           *
           *   (201) 555-0930  -> invalid! no dial or country code
           *   +1 201-555-0930 -> only full values are considered valid
           *
           * Country code is added by the PhoneField AFTER it is fully filled
           */
          isValidPhoneNumber(val),
      },
      url: {
        message: 'The :attribute must be a url.',
        rule: (val: any, params: any, validator: any) =>
          validator.helpers.testRegex(
            val,
            /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&%'()*+,;=.]+$/g
          ),
      },
      urlOrMailto: {
        message: 'The :attribute must be a valid URL or mailto: link.',
        rule: (val: any, params: any, validator: any) =>
          validator.helpers.testRegex(
            val,
            /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&%'()*+,;=.]+$/g
          ) ||
          validator.helpers.testRegex(
            val,
            /^mailto:[^\s@,]+@[^\s@,]+\.[^\s@,]+(,[^\s@,]+@[^\s@,]+\.[^\s@,]+)*(\?[^\s]*)?$/g
          ),
      },
      urlWithProtocol: {
        message: 'The :attribute must be a url.',
        rule: (val: any, params: any, validator: any) =>
          validator.helpers.testRegex(
            val,
            /(https?:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9])(:?\d*)\/?([a-z_\\/0-9\-#.]*)\??([a-z_\\/0-9\-#=&]*)/i
          ),
      },
    };
  }

  public check(key: string, value: any, rules: string) {
    return this.form.message(key, value, rules);
  }

  public getError(key: string, isSubmit: boolean) {
    return this.hasError(key, isSubmit)
      ? this.form.getErrorMessages()[key]
      : '';
  }

  public hasError(key: string, isSubmit: boolean) {
    return !this.form.fieldValid(key) && isSubmit;
  }

  public isValid() {
    const isValid = this.form.allValid();

    // TODO: temporary fix until we implement a new library
    if (!isValid && this.scrollOnInvalid) {
      scrollToError();
    }

    return isValid;
  }

  public setFormFieldsValidations(fields: [any]) {
    for (const field of fields) {
      this.form.message(field.name, field.state, field.validators);
    }
  }

  public showMessages() {
    return this.form.showMessages();
  }

  public reset() {
    this.form.purgeFields();
  }

  /**
   * For function components, the service is initialized without the
   * component parameter, so it defaults using "this" as the service class.
   * The simple-react-validator library expects to call a forceUpdate method,
   * so if FormValidatorService.forceUpdate doesn't exist, it will break.
   * So this method is just to avoid that issue.
   */
  public forceUpdate() {}
}
